// Install requirement for phones.
//
// On a phone or tablet the installed app is required, so a browser tab shows
// only how to install it: no install button (iOS has no install API, and the
// Android one cannot be relied on to fire) and no way to skip it. Desktop
// browsers are full clients and never see this.

import { useEffect, useState, type ReactNode } from 'react';
import { Center, Group, Image, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import {
  IconAdjustments,
  IconArrowDown,
  IconArrowUpRight,
  IconChartLine,
  IconCpu,
  IconDeviceMobileDown,
  IconDots,
  IconDotsVertical,
  IconShare2,
  IconSquarePlus,
} from '@tabler/icons-react';
import { useTranslation } from '@/i18n/I18nProvider';
import { SketchCard } from '@/components/primitives';
import { detectPlatform, isStandalone } from '@/lib/platform';

/** Whether the app is running as an installed PWA. */
export function useInstalled(): boolean {
  const [installed, setInstalled] = useState(() => isStandalone());
  useEffect(() => {
    const query = window.matchMedia('(display-mode: standalone)');
    const listener = (): void => setInstalled(isStandalone());
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, []);
  return installed;
}

/** Whether this is a phone browser tab that must install before continuing. */
export function useInstallRequired(): boolean {
  const installed = useInstalled();
  const [platform] = useState(() => detectPlatform());
  return platform !== 'desktop' && !installed;
}

/** A toolbar glyph as the phone draws it, so the step can be matched by eye. */
function Glyph({ children, tint }: { children: ReactNode; tint?: string }): ReactNode {
  return (
    <Center
      w={44}
      miw={44}
      h={44}
      style={{
        flexShrink: 0,
        borderRadius: 12,
        border: '1px solid var(--sensing-border)',
        background: 'var(--sensing-surface-raised)',
        color: tint ?? 'var(--sensing-ink)',
      }}
      aria-hidden
    >
      {children}
    </Center>
  );
}

/** One numbered step: the glyph to look for and what to do with it. */
function Step({
  number,
  glyph,
  children,
}: {
  number: number;
  glyph: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <Group gap="sm" wrap="nowrap" align="center">
      <Text fw={700} c="dimmed" w={14} ta="center" aria-hidden>
        {number}
      </Text>
      {glyph}
      <Stack gap={2} style={{ minWidth: 0 }}>
        {children}
      </Stack>
    </Group>
  );
}

// Safari draws its toolbar glyphs in the system blue.
const SAFARI_BLUE = '#007aff';

/**
 * The install requirement and the steps for this phone; nothing elsewhere.
 * `compact` is the variant inside the full install screen, which already says
 * what the app is: a one-line title and tighter steps.
 */
export function InstallPrompt({ compact = false }: { compact?: boolean }): ReactNode {
  const t = useTranslation();
  const required = useInstallRequired();
  const [platform] = useState(() => detectPlatform());

  if (!required) return null;

  const appIcon = (
    <Image
      src="/icons/icon-192.png"
      alt=""
      w={44}
      miw={44}
      h={44}
      radius={10}
      style={{ flexShrink: 0 }}
      aria-hidden
    />
  );

  return (
    <SketchCard>
      <Stack gap={compact ? 'sm' : 'lg'}>
        <Stack gap={4}>
          <Title order={compact ? 3 : 2} className="sensing-hand">
            {t('install.title')}
          </Title>
          {compact ? null : (
            <Text size="sm" c="dimmed">
              {t('install.required')}
            </Text>
          )}
        </Stack>
        <Stack gap={compact ? 'sm' : 'md'} component="ol" m={0} p={0} style={{ listStyle: 'none' }}>
          {platform === 'ios' ? (
            <>
              <li>
                <Step
                  number={1}
                  glyph={
                    <Glyph tint={SAFARI_BLUE}>
                      <IconShare2 size={24} stroke={1.8} />
                    </Glyph>
                  }
                >
                  <Text fw={600}>{t('install.iosShare')}</Text>
                  <Group gap={4} wrap="nowrap">
                    <Text size="xs" c="dimmed">
                      {t('install.iosShareHint')}
                    </Text>
                    <IconDots size={16} color={SAFARI_BLUE} aria-hidden />
                  </Group>
                </Step>
              </li>
              <li>
                <Step
                  number={2}
                  glyph={
                    <Glyph>
                      <IconSquarePlus size={24} stroke={1.8} />
                    </Glyph>
                  }
                >
                  <Text fw={600}>{t('install.iosAdd')}</Text>
                </Step>
              </li>
            </>
          ) : (
            <>
              <li>
                <Step
                  number={1}
                  glyph={
                    <Glyph>
                      <IconDotsVertical size={24} stroke={1.8} />
                    </Glyph>
                  }
                >
                  <Text fw={600}>{t('install.androidMenu')}</Text>
                </Step>
              </li>
              <li>
                <Step
                  number={2}
                  glyph={
                    <Glyph>
                      <IconDeviceMobileDown size={24} stroke={1.8} />
                    </Glyph>
                  }
                >
                  <Text fw={600}>{t('install.androidAdd')}</Text>
                </Step>
              </li>
            </>
          )}
          <li>
            <Step number={3} glyph={appIcon}>
              <Text fw={600}>{t('install.open')}</Text>
            </Step>
          </li>
        </Stack>
      </Stack>
    </SketchCard>
  );
}

/** One line of what the app does, with its icon. */
function Feature({ icon, children }: { icon: ReactNode; children: ReactNode }): ReactNode {
  return (
    <Group gap="sm" wrap="nowrap">
      <ThemeIcon size={30} radius="xl" variant="light" color="foliage" aria-hidden>
        {icon}
      </ThemeIcon>
      <Text size="sm" lh={1.3}>
        {children}
      </Text>
    </Group>
  );
}

/**
 * The whole sign-in screen of a phone browser tab: what the app is, how to
 * install it, and a pointer to where the browser keeps the button to start.
 * `extra` holds a language choice when the phone's language is not one the
 * app speaks.
 */
export function InstallScreen({ extra }: { extra?: ReactNode }): ReactNode {
  const t = useTranslation();
  const [platform] = useState(() => detectPlatform());

  // A fixed frame, like the installed app's: the document never scrolls
  // (`sensing-app-frame` in global.css), and this screen is exactly the
  // browser's small viewport. `svh` leaves out the browser's own toolbars;
  // `dvh` would include the area behind Safari's floating toolbar and push
  // the bottom of the screen under it. A phone too short for the content
  // scrolls this frame, not the page.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('sensing-app-frame');
    return () => root.classList.remove('sensing-app-frame');
  }, []);

  return (
    <Stack
      gap="lg"
      justify="space-between"
      maw={480}
      mx="auto"
      px="md"
      className="sensing-install-frame"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top) + 1.25rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)',
        position: 'relative',
      }}
    >
      {platform === 'android' ? (
        // Chrome keeps its menu at the top right.
        <Group
          gap={2}
          wrap="nowrap"
          c="dimmed"
          style={{ position: 'absolute', top: 'calc(env(safe-area-inset-top) + 6px)', right: 12 }}
          aria-hidden
        >
          <Text size="xs">{t('install.menuHere')}</Text>
          <IconArrowUpRight size={18} className="sensing-nudge-up" />
        </Group>
      ) : null}

      {/* Spacer: with the bottom pointer, keeps the content centred as one
          block on tall screens instead of splitting it apart. */}
      <div aria-hidden />

      <Stack gap="md">
        <Stack gap={6} align="center">
          <Image
            src="/icons/icon-192.png"
            alt=""
            w={64}
            h={64}
            radius={15}
            style={{
              border: '1px solid var(--sensing-border)',
              boxShadow: '0 6px 18px rgba(42, 36, 25, 0.16)',
            }}
            aria-hidden
          />
          <Stack gap={0} align="center">
            <Title order={1} className="sensing-hand" ta="center" fz="2rem" lh={1.2}>
              Sensing
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              {t('app.tagline')}
            </Text>
          </Stack>
          <Stack gap={8} w="100%" mt={6} className="sensing-install-features">
            <Feature icon={<IconChartLine size={17} />}>{t('install.featureReadings')}</Feature>
            <Feature icon={<IconCpu size={17} />}>{t('install.featureDevices')}</Feature>
            <Feature icon={<IconAdjustments size={17} />}>{t('install.featureRemote')}</Feature>
          </Stack>
        </Stack>

        <Stack gap="sm">
          {extra}
          <InstallPrompt compact />
        </Stack>
      </Stack>

      {platform === 'ios' ? (
        // Safari keeps Share in its toolbar at the bottom of the screen.
        <Group gap={6} justify="center" c="dimmed" wrap="nowrap" aria-hidden>
          <Text size="xs">{t('install.toolbarBelow')}</Text>
          <IconArrowDown size={18} className="sensing-nudge-down" />
        </Group>
      ) : (
        <div aria-hidden />
      )}
    </Stack>
  );
}

/**
 * Gate for screens that need the installed app on a phone. Desktop is never
 * gated.
 */
export function MobileInstallGate({ children }: { children: ReactNode }): ReactNode {
  const required = useInstallRequired();
  return required ? <InstallPrompt /> : children;
}
